import React, { useRef } from 'react';
import { Modal, Field } from '../../components/ui';
import { INVITE_STATUSES, VISIBILITIES, fmtDate, normaliseText, nameInput, tenDigits, MAX_NAME, isWeddingFunction } from '../../utils/helpers';
import { MAX_FAMILY_SIZE } from './useMembers';
import NumberChoice from './NumberChoice';
import PeopleSearch from './PeopleSearch';
import { ContactField, ContactSuggestList } from './ContactSuggest';

const ten = tenDigits;

// Shared add/edit modal — identical on desktop and mobile (the Modal is already
// responsive). All state/handlers come from the useMembers hook (vm).
// Desktop guest form: wide, dense 4-column modal so the whole form fits one screen.
export default function MemberFormDesktop({ vm }) {
  const {
    pickerFunctions, form, setF, setMobile, editing, fnName, me, selectedFunctionIds, toggleFunction,
    familyMode, addMode, contactSuggest, existingNotice, deliveryNames, cardHeadOptions, familyHeadExisting, familyMemberExisting, party,
    sameNameGuests, mergePick, toggleMergePick, mergeDuplicates, merging, familyHeadNote, setAddMode, distinctPeople, dupes, blockedSameFn,
    canImport, importContact, pickExisting, save, saving, setOpen,
    isMarriage, lockedSide, knownSurnames, knownNames,
  } = vm;

  return (
    <Modal
      wide dense widest={!editing && familyMode} className={!editing && familyMode ? 'family-modal' : ''}
      title={editing ? (existingNotice ? `Already a guest — ${existingNotice}` : 'Edit Guest') : (familyMode ? 'Add Family' : 'Add Guest')}
      onClose={() => setOpen(false)}
      footer={<>
        <button className="btn light" onClick={() => setOpen(false)}>Cancel</button>
        {!editing && familyMode && (
          <button className="btn light" disabled={saving || blockedSameFn} onClick={() => save({ addNext: true })}
            title="Save this family and start the next one with the same functions">
            Save &amp; next family
          </button>
        )}
        <button className="btn primary" disabled={saving || blockedSameFn} onClick={() => save()}>
          {saving ? 'Saving...' : (editing ? 'Save' : (familyMode ? 'Add Family' : 'Add Guest'))}
        </button>
      </>}
    >
      {!editing && !familyMode && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button className={`btn sm ${addMode === 'new' ? 'primary' : 'light'}`} onClick={() => setAddMode('new')}>New person</button>
          <button className={`btn sm ${addMode === 'existing' ? 'primary' : 'light'}`}
            onClick={() => setAddMode('existing')} disabled={!distinctPeople.length}>Pick existing</button>
          {canImport && (
            <button className="btn sm light" onClick={importContact} title="Pick a contact from your phone book">
              📇 From Contacts
            </button>
          )}
        </div>
      )}

      {!editing && !familyMode && addMode === 'existing' && (
        <Field label="Existing member (re-invite to another function)" type="select" value=""
          options={distinctPeople.map(p => ({ value: p.GuestId, label: `${[p.Surname, p.GuestName, p.FatherHusband].filter(Boolean).join(' ')} · ${p.MobileNumber || ''}` }))}
          onChange={pickExisting} />
      )}

      {editing && existingNotice && (
        <div className="existing-guest-note">
          <strong>{existingNotice}</strong> is already a guest of this event — the functions ticked below are the ones
          already allotted to them. Tick or untick, fix the card, then <strong>Save</strong>.
        </div>
      )}

      {editing && sameNameGuests.length > 0 && (
        <div className="merge-dupes">
          <strong>Same name - {sameNameGuests.length} more guest{sameNameGuests.length > 1 ? 's' : ''} called {[editing.Surname, editing.GuestName].filter(Boolean).join(' ')}.</strong>
          <span className="muted"> Tick the ones that are this same person and merge them here (this guest, {editing.MobileNumber || 'no number'}, is kept).</span>
          <div className="merge-dupes-list">
            {sameNameGuests.map(g => (
              <label key={g.GuestId}>
                <input type="checkbox" checked={mergePick.has(String(g.GuestId))} onChange={() => toggleMergePick(g.GuestId)} />
                <strong>{[g.Surname, g.GuestName, g.FatherHusband].filter(Boolean).join(' ')}</strong>
                <span>{g.MobileNumber || 'no number'}</span>
                <span className="muted">{g.FunctionNames || ''}</span>
                {Number(g.FamilyId || 0) === Number(g.GuestId) && <span className="chip ok">Head</span>}
              </label>
            ))}
          </div>
          <button type="button" className="btn primary sm" disabled={merging || mergePick.size === 0} onClick={mergeDuplicates}>
            {merging ? 'Merging…' : `Merge ${mergePick.size || ''} into this guest`}
          </button>
        </div>
      )}

      {/* Someone already known (any event / synced contacts) → fills the fields. */}
      {!editing && !familyMode && addMode === 'new' && <PeopleSearch vm={vm} />}

      {!(familyMode && !editing) && <>
        <div>
          <label className="field">
            <span>Functions <b>*</b></span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px' }}>
              {pickerFunctions.length === 0 && <span className="muted" style={{ fontSize: 12 }}>All functions of this event are over — nothing to invite to.</span>}
              {pickerFunctions.map(f => (
                <label key={f.FunctionId} title={f.past ? 'This function is over' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: f.past ? 'default' : 'pointer', fontWeight: 500, color: 'var(--ink)', opacity: f.past ? 0.55 : 1 }}>
                  <input
                    type="checkbox"
                    checked={selectedFunctionIds.includes(String(f.FunctionId))}
                    disabled={f.past}
                    onChange={() => toggleFunction(f.FunctionId)}
                    style={{ width: 'auto' }}
                  />
                  <span>{f.FunctionName} <span className="fn-count">({vm.fnCount(f.FunctionId)})</span>{f.past ? ' (over)' : ''}</span>
                </label>
              ))}
            </div>
          </label>
        </div>
        {/* Boxes sized to their content: names ≈ 15 letters, mobile 10 digits. */}
        <div className="gf-row">
          {editing ? (
            <>
              <div className="gf-name"><Field label="Surname" value={form.Surname} suggest={knownSurnames} onChange={(v) => setF('Surname', nameInput(v))} /></div>
              <div className="gf-name"><Field label="Name" value={form.GuestName} required suggest={knownNames} onChange={(v) => setF('GuestName', nameInput(v))} /></div>
              <div className="gf-name"><Field label="Father / Husband" value={form.FatherHusband} onChange={(v) => setF('FatherHusband', normaliseText(v))} /></div>
              <div className="gf-mobile"><Field label="Mobile" value={form.MobileNumber} placeholder="optional" onChange={setMobile} /></div>
              {/* Who invited this guest - shown, never edited. */}
              {(() => {
                const name = (editing.InvitedByName || '').trim() || (editing.AddedByName || '').trim();
                const mob = editing.InvitedByMobile || editing.AddedByMobile || '';
                return (
                  <label className="field gf-added">Added by
                    <div className="gf-added-box">
                      <strong>{name || 'Unknown'}</strong>
                      {mob && <span className="muted"> · {mob}</span>}
                      {Number(editing.AddedByGuest) === 1 && <span className="chip guest-added">Guest</span>}
                      {editing.CreatedDate && <span className="muted"> · {fmtDate(editing.CreatedDate)}</span>}
                    </div>
                  </label>
                );
              })()}
            </>
          ) : (
            <>
              {/* Typing in any of these lists matching contacts from MY phone book;
                  picking one fills all four (useContactSuggest). */}
              <div className="gf-name"><ContactField cs={contactSuggest} target="head" field="Surname" label="Surname" value={form.Surname}
                context={`${form.GuestName || ''} ${form.FatherHusband || ''}`} onChange={(v) => setF('Surname', nameInput(v))} /></div>
              <div className="gf-name"><ContactField cs={contactSuggest} target="head" field="GuestName" label="Name" required value={form.GuestName}
                context={`${form.Surname || ''} ${form.FatherHusband || ''}`} onChange={(v) => setF('GuestName', nameInput(v))} /></div>
              <div className="gf-name"><ContactField cs={contactSuggest} target="head" field="FatherHusband" label="Father / Husband" value={form.FatherHusband}
                context={`${form.Surname || ''} ${form.GuestName || ''}`} onChange={(v) => setF('FatherHusband', normaliseText(v))} /></div>
              {/* Optional: many relatives are invited in person, not on WhatsApp. */}
              <div className="gf-mobile"><ContactField cs={contactSuggest} target="head" field="MobileNumber" label="Mobile" value={form.MobileNumber}
                placeholder="optional" inputMode="numeric" onChange={setMobile} /></div>
            </>
          )}
        </div>

        {/* A business associate is invited as a firm: the card names the owner
            with the company under it. City is kept for everyone. */}
        <div className="gf-row">
          <div className="gf-select"><Field label="Guest is" type="select" value={form.GuestType || 'Family'}
            options={[{ value: 'Family', label: 'Family / friend' }, { value: 'Business', label: 'Business associate' }]}
            onChange={(v) => setF('GuestType', v || 'Family')} /></div>
          {String(form.GuestType) === 'Business' && (
            <div className="gf-name"><Field label="Firm / Company" value={form.CompanyName}
              placeholder="printed under the owner's name" onChange={(v) => setF('CompanyName', normaliseText(v))} /></div>
          )}
          <div className="gf-name"><Field label="City" value={form.City} onChange={(v) => setF('City', normaliseText(v))} /></div>
        </div>
      </>}

      {!editing && familyMode && <FamilyGrid vm={vm} />}

      {!editing && familyMode && familyHeadNote && <div className="existing-guest-note">{familyHeadNote}</div>}

      {!editing && familyMode && (familyHeadExisting || party.some(m => familyMemberExisting(m))) && (
        <div className="existing-guest-note">
          <strong>Already guests - they will be merged, not added twice:</strong>
          {familyHeadExisting && <> head <strong>{[familyHeadExisting.Surname, familyHeadExisting.GuestName].filter(Boolean).join(' ')}</strong> becomes this family's head;</>}
          {party.filter(m => familyMemberExisting(m)).length > 0 && (
            <> {party.filter(m => familyMemberExisting(m)).map(m => m.GuestName).join(', ')} {party.filter(m => familyMemberExisting(m)).length === 1 ? 'is' : 'are'} moved into this family (marked ↻).</>
          )}
          {' '}Everyone keeps their functions; the ticked ones are added.
        </div>
      )}

      {!editing && !familyMode && dupes.length > 0 && (
        <div className="card card-pad" style={{ marginTop: 10, background: blockedSameFn ? '#fee2e2' : '#fef3c7', fontSize: 13 }}>
          <strong>Already added in this event:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {dupes.map(d => (
              <li key={d.GuestId}>
                {(d.InvitedByName || '').trim() || 'Someone'} - {d.FunctionName || fnName(d.FunctionId)}
                {String(d.CreatedBy) === String(me) ? ' (you)' : ''}
              </li>
            ))}
          </ul>
          {blockedSameFn
            ? <div style={{ marginTop: 6, color: 'var(--danger)' }}>You already added this member to this function. Change the function to add them elsewhere.</div>
            : <div style={{ marginTop: 6 }}>The same person can be invited by more than one member. You can still add them to your own list.</div>}
        </div>
      )}

      {/* ── Invitation card: own card, or on another family head's card ── */}
      <div className="guest-card-section">
        <div className="guest-card-section-title">Invitation card</div>
        <div className="gf-row">
          {!familyMode && (
            <div className="gf-select"><Field label="Card" type="select" value={form.CardMode}
              options={[{ value: 'own', label: 'Own card (head)' }, { value: 'family', label: 'On the card of…' }]}
              onChange={(v) => setF('CardMode', v || 'own')} /></div>
          )}
          {!familyMode && form.CardMode === 'family' ? (
            <>
              <div className="gf-wide">
                <label className="field">Head of that card
                  <input list="card-head-options" value={(cardHeadOptions.find(o => o.value === String(form.CardFamilyId)) || {}).label || form.CardHeadText || ''}
                    placeholder="type a name — family heads and guests"
                    onChange={(e) => {
                      const o = cardHeadOptions.find(x => x.label === e.target.value);
                      setF('CardFamilyId', o ? o.value : '');
                      setF('CardHeadText', e.target.value);
                    }} />
                </label>
                <datalist id="card-head-options">
                  {cardHeadOptions
                    .filter(o => !editing || !(o.guestIds || []).map(String).includes(String(editing.GuestId)))
                    .filter(o => !editing || o.value !== String(editing.GuestId))
                    .map(o => <option key={o.value} value={o.label} />)}
                </datalist>
              </div>
              {(() => {
                const f = cardHeadOptions.find(x => x.value === String(form.CardFamilyId));
                return f ? (
                  <div className="muted guest-card-of">
                    Card name: <strong>{String(f.head.CardName || '').trim() || [f.head.Surname, f.head.GuestName].filter(Boolean).join(' ')}</strong>
                    {f.head.CardDeliveredByName ? ` · delivered by ${f.head.CardDeliveredByName}` : ''}
                  </div>
                ) : null;
              })()}
            </>
          ) : (
            <>
              {/* Shows the guest's own name until it is typed over; × clears it for a new name. */}
              <label className="field gf-wide">Name on the card
                <div className="cardname-box">
                  <input value={form.CardNameAuto ? [form.Surname, form.GuestName, form.FatherHusband].filter(Boolean).join(' ') : form.CardName}
                    placeholder="type the name printed on the card"
                    onChange={(e) => { setF('CardName', normaliseText(e.target.value)); setF('CardNameAuto', false); }} />
                  {(form.CardNameAuto ? [form.Surname, form.GuestName].some(Boolean) : !!form.CardName) && (
                    <button type="button" className="cardname-clear" title="Clear - type a different name for the card"
                      onClick={() => { setF('CardName', ''); setF('CardNameAuto', false); }}>×</button>
                  )}
                  {!form.CardNameAuto && (
                    <button type="button" className="cardname-reset" title="Use the guest's own name again"
                      onClick={() => { setF('CardName', ''); setF('CardNameAuto', true); }}>↺</button>
                  )}
                </div>
              </label>
              <label className="field gf-num" title="Extra invitation cards this head wants to hand out — names unknown, Reception only">Extra cards
                <input type="number" min="0" max="99" value={form.ExtraCards} placeholder="0"
                  onChange={e => setF('ExtraCards', e.target.value.replace(/\D/g, '').slice(0, 2))} />
              </label>
              <label className="field gf-name">Delivered by
                <input list="card-delivery-names" value={form.CardDeliveredByName} placeholder="who takes the card"
                  onChange={e => setF('CardDeliveredByName', normaliseText(e.target.value))} />
              </label>
            </>
          )}
        </div>
        <datalist id="card-delivery-names">{(deliveryNames || []).map(n => <option key={n} value={n} />)}</datalist>
      </div>

      <div className="gf-row" style={{ marginTop: 6 }}>
        <div className="gf-select" title="Private = only you (and the Event Admin) see this guest · Common = all members see it">
          <Field label="Visibility" type="select" value={form.Visibility} options={VISIBILITIES} onChange={(v) => setF('Visibility', v)} />
        </div>
        {/* The side is the one on your profile — shown, never asked. */}
        {isMarriage && (
          <label className="field gf-short">Side
            <input value={lockedSide || form.Side || 'Common'} readOnly disabled title="Set on My Profile" />
          </label>
        )}
        {editing && (
          <>
            <div className="gf-name"><Field label="Relation" value={form.Relation} onChange={(v) => setF('Relation', normaliseText(v))} /></div>
            <div className="gf-name"><Field label="Group" value={form.GroupName} onChange={(v) => setF('GroupName', normaliseText(v))} /></div>
            <div className="gf-select"><Field label="Invite Status" type="select" value={form.InviteStatus} options={INVITE_STATUSES} onChange={(v) => setF('InviteStatus', v)} /></div>
            <div className="gf-select"><Field label="Invited Via" type="select" value={form.InvitedVia} options={['WhatsApp', 'Call', 'Card']} onChange={(v) => setF('InvitedVia', v)} /></div>
          </>
        )}
        <div className="gf-fill"><Field label="Remarks" value={form.Remarks} onChange={(v) => setF('Remarks', v)} /></div>
      </div>
      <NumberChoice vm={vm} />
    </Modal>
  );
}

// Add Family as one table: row 1 is the head - its ticks are the family's
// functions - and every further row is a member, whose ticks can only be a
// subset of the head's (a child who comes to the Haldi only). Enter jumps to
// the same box one row down; on the last row it adds a row. Blank rows are
// ignored on save. A member left without a surname takes the head's.
function FamilyGrid({ vm }) {
  const {
    pickerFunctions, form, setF, setMobile, selectedFunctionIds, toggleFunction,
    party, addParty, setPartyField, removeParty, togglePartyFunction, partyFunctionIds,
    canImport, importContact, importPartyContact, knownSurnames, knownNames, contactSuggest: cs,
    familyHeadExisting, familyMemberExisting, existingFamilyMembers, rowHasFunction,
  } = vm;
  const tableRef = useRef(null);
  const focusCell = (row, col) => {
    const el = tableRef.current?.querySelector(`[data-f="${row}-${col}"]`);
    if (el) { el.focus(); el.select?.(); }
  };
  // row 0 = head, 1.. = members
  const onKey = (e, row, col) => {
    if (cs.keyDown(e)) return; // ↓ ↑ Enter Esc go to the contact list when it is open
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (row >= party.length) {
      if (party.length >= MAX_FAMILY_SIZE - 1) return;
      addParty();
      setTimeout(() => focusCell(row + 1, col), 0);
    } else focusCell(row + 1, col);
  };
  const full = existingFamilyMembers.length + party.length >= MAX_FAMILY_SIZE - 1;
  // The family's size counts who is already in it, so "of max 9" is honest.
  const people = 1 + existingFamilyMembers.length + party.filter(m => String(m.GuestName || '').trim()).length;

  return (
    <div className="family-grid">
      <datalist id="sg-family-surnames">{knownSurnames.map(s => <option key={s} value={s} />)}</datalist>
      <datalist id="sg-family-names">{knownNames.map(s => <option key={s} value={s} />)}</datalist>
      {pickerFunctions.length === 0 && (
        <div className="muted" style={{ fontSize: 12 }}>All functions of this event are over — nothing to invite to.</div>
      )}
      <div className="family-grid-scroll">
        <table className="tbl compact family-grid-table" ref={tableRef}>
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Surname</th>
              <th>Name *</th>
              <th>Phone</th>
              {pickerFunctions.map(f => (
                <th key={f.FunctionId} className="fn-col" title={f.FunctionName}>
                  {isWeddingFunction(f) ? '💍 ' : ''}{f.FunctionName} <span className="fn-count">({vm.fnCount(f.FunctionId)})</span>
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr className="family-head-row">
              <td className="num"><span className="chip ok" title="Family head - their ticks are the family's functions">Head</span>
                {familyHeadExisting && <span className="fam-existing" title="Already a guest - made this family's head (no duplicate)">↻</span>}</td>
              <td className="cs-wrap"><input data-f="0-0" value={form.Surname} maxLength={MAX_NAME} placeholder="Surname" autoComplete="off"
                onChange={e => { setF('Surname', nameInput(e.target.value)); cs.typed('head', 'Surname', e.target.value, form.GuestName); }}
                onKeyDown={e => onKey(e, 0, 0)} onBlur={cs.close} />
                <ContactSuggestList cs={cs} target="head" field="Surname" /></td>
              <td className="cs-wrap"><input data-f="0-1" value={form.GuestName} maxLength={MAX_NAME} placeholder="Head name" autoFocus autoComplete="off"
                onChange={e => { setF('GuestName', nameInput(e.target.value)); cs.typed('head', 'GuestName', e.target.value, form.Surname); }}
                onKeyDown={e => onKey(e, 0, 1)} onBlur={cs.close} />
                <ContactSuggestList cs={cs} target="head" field="GuestName" /></td>
              <td className="cs-wrap"><input data-f="0-2" className="phone" value={form.MobileNumber} placeholder="10 digits" inputMode="numeric" autoComplete="off"
                onChange={e => { setMobile(e.target.value); cs.typed('head', 'MobileNumber', e.target.value); }}
                onKeyDown={e => onKey(e, 0, 2)} onBlur={cs.close} />
                <ContactSuggestList cs={cs} target="head" field="MobileNumber" /></td>
              {pickerFunctions.map(f => (
                <td key={f.FunctionId} className="fn-col">
                  <input type="checkbox" disabled={f.past} title={f.FunctionName}
                    checked={selectedFunctionIds.includes(String(f.FunctionId))}
                    onChange={() => toggleFunction(f.FunctionId)} />
                </td>
              ))}
              <td className="row-tools">
                {canImport && <button type="button" className="icon-btn" title="Pick from the phone book" onClick={importContact}>📇</button>}
              </td>
            </tr>
            {/* The family this head already has: shown so the user sees who is
                there before adding more. These rows are not saved again. */}
            {existingFamilyMembers.map((m, i) => (
              <tr key={`has-${m.GuestId}`} className="fam-has-row">
                <td className="num" title="Already in this family">{i + 2}</td>
                <td>{m.Surname || ''}</td>
                <td>{m.GuestName}<span className="fam-existing" title="Already in this family - stays as it is"> ↻</span></td>
                <td>{m.MobileNumber || '—'}</td>
                {pickerFunctions.map(f => (
                  <td key={f.FunctionId} className="fn-col">
                    <input type="checkbox" checked={rowHasFunction(m, f.FunctionId)} readOnly disabled
                      title={`${f.FunctionName} - change it from the guest list`} />
                  </td>
                ))}
                <td className="row-tools" />
              </tr>
            ))}
            {party.map((m, i) => {
              const own = partyFunctionIds(m);
              const row = i + 1;
              return (
                <tr key={i}>
                  <td className="num muted">{i + 2}
                    {familyMemberExisting(m) && <span className="fam-existing" title="Already a guest - moved into this family (no duplicate)">↻</span>}</td>
                  <td className="cs-wrap"><input data-f={`${row}-0`} value={m.Surname} maxLength={MAX_NAME} autoComplete="off"
                    placeholder={form.Surname || 'Surname'}
                    onChange={e => { setPartyField(i, 'Surname', nameInput(e.target.value)); cs.typed(i, 'Surname', e.target.value, m.GuestName); }}
                    onKeyDown={e => onKey(e, row, 0)} onBlur={cs.close} />
                    <ContactSuggestList cs={cs} target={i} field="Surname" /></td>
                  <td className="cs-wrap"><input data-f={`${row}-1`} value={m.GuestName} maxLength={MAX_NAME} placeholder="Name" autoComplete="off"
                    onChange={e => { setPartyField(i, 'GuestName', nameInput(e.target.value)); cs.typed(i, 'GuestName', e.target.value, m.Surname || form.Surname); }}
                    onKeyDown={e => onKey(e, row, 1)} onBlur={cs.close} />
                    <ContactSuggestList cs={cs} target={i} field="GuestName" /></td>
                  <td className="cs-wrap"><input data-f={`${row}-2`} className="phone" value={m.MobileNumber} placeholder="optional" inputMode="numeric" autoComplete="off"
                    onChange={e => { setPartyField(i, 'MobileNumber', ten(e.target.value)); cs.typed(i, 'MobileNumber', e.target.value); }}
                    onKeyDown={e => onKey(e, row, 2)} onBlur={cs.close} />
                    <ContactSuggestList cs={cs} target={i} field="MobileNumber" /></td>
                  {pickerFunctions.map(f => {
                    const headHas = selectedFunctionIds.includes(String(f.FunctionId));
                    return (
                      <td key={f.FunctionId} className="fn-col">
                        <input type="checkbox" disabled={!headHas} title={headHas ? f.FunctionName : 'Tick it for the head first'}
                          checked={headHas && own.includes(String(f.FunctionId))}
                          onChange={() => togglePartyFunction(i, f.FunctionId)} />
                      </td>
                    );
                  })}
                  <td className="row-tools">
                    {canImport && (
                      <button type="button" className="icon-btn" tabIndex={-1} title="Pick from the phone book"
                        onClick={() => importPartyContact(i)}>📇</button>
                    )}
                    <button type="button" className="icon-btn danger" tabIndex={-1} title="Remove this line"
                      onClick={() => removeParty(i)}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="family-grid-foot">
        <button type="button" className="btn light sm" onClick={addParty} disabled={full}>+ Member</button>
        <span className="muted">
          {people} of max {MAX_FAMILY_SIZE} people · Enter = next line · blank lines are skipped · a member without a surname takes the head's
        </span>
      </div>
    </div>
  );
}
